import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import type { ShopperProducts } from '@/scapi';
import { NavLink } from '@/components/link';
import { getCategoryNavTarget } from '@/lib/category-nav-link';

type CategoryNavLinkProps = Omit<ComponentPropsWithoutRef<'a'>, 'href'> & {
    category: ShopperProducts.schemas['Category'];
    children?: ReactNode;
};

/**
 * Renders a site-context-aware link for a category flyout entry.
 * Resolves `c_linkType` / `c_linkValue` via {@link getCategoryNavTarget}.
 */
export const CategoryNavLink = forwardRef<HTMLAnchorElement, CategoryNavLinkProps>(
    ({ category, children, ...props }, ref) => {
        const target = getCategoryNavTarget(category);
        const content = children ?? category.name;

        if (target.kind === 'external') {
            return (
                <a target="_blank" rel="noopener noreferrer" ref={ref} {...props} href={target.href}>
                    {content}
                </a>
            );
        }

        return (
            <NavLink prefetch="intent" ref={ref} {...props} to={target.to}>
                {content}
            </NavLink>
        );
    }
);

CategoryNavLink.displayName = 'CategoryNavLink';
